/**
 * Pre-flight thresholds (SD-13) — user-adjustable numeric bounds each check
 * reads, so the same pre-flight reads stricter at a tight club and looser on
 * a rough hotspot. MACHINE TRUTH: a venue's WiFi is a property of the room +
 * this iPad, never the project — stored per-device in localStorage, never
 * synced (STAGE-DESIGN SD-13).
 */

import { createSignal } from 'solid-js';

export interface PreflightThresholds {
  /** Latency p95 at/above this (ms) is a WARN. Default 30. */
  rttWarnMs: number;
  /** Latency p95 at/above this (ms) is a FAIL. Default 100. */
  rttFailMs: number;
  /** How many /health samples the latency check takes. Default 20. */
  latencySamples: number;
  /** Battery at/below this (%) is a WARN (where the API exists). Default 20. */
  batteryWarnPct: number;
  /** Clock age at/above this (ms) counts the transport stale. Default 2000. */
  clockStaleFailMs: number;
}

export const PREFLIGHT_DEFAULTS: PreflightThresholds = {
  rttWarnMs: 30,
  rttFailMs: 100,
  latencySamples: 20,
  batteryWarnPct: 20,
  clockStaleFailMs: 2000,
};

const KEY = 'opbuttons.preflight.thresholds.v1';

function load(): PreflightThresholds {
  if (typeof localStorage === 'undefined') return { ...PREFLIGHT_DEFAULTS };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...PREFLIGHT_DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<PreflightThresholds>;
    // Tolerate partial / malformed blobs — fill any missing field from defaults.
    return { ...PREFLIGHT_DEFAULTS, ...sanitize(parsed) };
  } catch {
    return { ...PREFLIGHT_DEFAULTS };
  }
}

/** Keep only finite positive numbers; drop anything else so a corrupt blob
 *  can't poison a threshold. */
function sanitize(p: Partial<PreflightThresholds>): Partial<PreflightThresholds> {
  const out: Partial<PreflightThresholds> = {};
  for (const k of Object.keys(PREFLIGHT_DEFAULTS) as (keyof PreflightThresholds)[]) {
    const v = p[k];
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) out[k] = v;
  }
  return out;
}

const [thresholds, setThresholdsSig] = createSignal<PreflightThresholds>(load());
export { thresholds };

export function setThreshold<K extends keyof PreflightThresholds>(key: K, value: number): void {
  if (!Number.isFinite(value) || value <= 0) return;
  const next = { ...thresholds(), [key]: value };
  setThresholdsSig(next);
  persist(next);
}

export function resetThresholds(): void {
  const next = { ...PREFLIGHT_DEFAULTS };
  setThresholdsSig(next);
  persist(next);
}

function persist(t: PreflightThresholds): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(KEY, JSON.stringify(t)); } catch { /* private mode / quota */ }
}
